import { invariant } from '@mimir/common/util/invariant';
import type { Logger } from '@mimir/common/types/logger';
import { AudioFrameV2, AudioInfoV2 } from '@mimir/cutter/frameProvider/frameProvider';
import { calculateRawAudioFrameBufferSize } from '@mimir/common/transcoding/rawFrame';
import { RefCountedBuffer, BufferCache } from '@mimir/cutter/frameProvider/refCountedBuffer';

export interface AudioCache {
  sampleRate(): number | null;
  push(audioFrame: AudioFrameV2): void;
  get(sampleOffset: number, sampleCount: number): AudioFrameV2 | null;
  remove(startSampleOffset: number, endSampleOffset: number): void;
  ranges(): { start: number, end: number }[];
  clear(): void;
  close(): void;
}

export function formatRanges(ranges: { start: number, end: number }[]): string {
  return `(${ranges.map((r) => `[${r.start}, ${r.end}>`).join(', ')})`;
}

export function createAudioCache(logger: Logger, bufferCache: BufferCache) {
  return createAudioCacheInternal(logger, bufferCache);
}

// ----

interface CacheEntry {
  sampleOffset: number,
  sampleCount: number,
  bufferOffset: number,
  buffer: RefCountedBuffer,
}

function createAudioCacheInternal(logger: Logger, bufferCache: BufferCache) {
  const cache : CacheEntry[] = [];

  let info : AudioInfoV2 | null = null;

  return {
    sampleRate(): number | null {
      return info?.sampleRate ?? null
    },
    push(audioFrame: AudioFrameV2): void {
      logger.debug(`AudioCache.push() {`);
      if (info === null) {
        info = {
          sampleRate: audioFrame.sampleRate,
          channelCount: audioFrame.channelCount,
          format: audioFrame.format,
        };
      } else {
        if (info.sampleRate !== audioFrame.sampleRate) {
          throw new Error('Inconsistent sample rate pushed into AudioCache');
        }
        if (info.channelCount !== audioFrame.channelCount) {
          throw new Error('Inconsistent channel count pushed into AudioCache');
        }
        if (info.format !== audioFrame.format) {
          throw new Error('Inconsistent audio format pushed into AudioCache');
        }
      }
      const expectedBufferSize = calculateRawAudioFrameBufferSize(audioFrame, audioFrame.sampleCount);
      if (expectedBufferSize !== audioFrame.buffer.size()) {
        throw new Error(`Bad buffer size: ${expectedBufferSize} != ${audioFrame.buffer.size()}`);
      }
      deleteRange(logger, audioFrame.sampleOffset, audioFrame.sampleCount, cache, info);
      cache.push({
        sampleOffset: audioFrame.sampleOffset,
        sampleCount: audioFrame.sampleCount,
        bufferOffset: 0,
        buffer: audioFrame.buffer,
      });
      cache.sort((a, b) => a.sampleOffset - b.sampleOffset);
      logger.debug('}');
    },
    get(sampleOffset: number, sampleCount: number): AudioFrameV2 | null {
      logger.debug(`AudioCache.get(sampleOffset=${sampleOffset}, sampleCount=${sampleCount}) {`);
      if (info === null) {
        return null;
      }
      const audioFrame = getRange(logger, sampleOffset, sampleCount, info, cache, bufferCache);
      logger.debug('}');
      return audioFrame;
    },
    ranges(): { start: number, end: number }[] {
      let ranges : { start: number, end: number }[] = [];
      let currentRange : { start: number, end: number } | null = null;
      for (const frame of cache) {
        if (currentRange !== null) {
          if (currentRange.end !== frame.sampleOffset) {
            ranges.push(currentRange);
            currentRange = null;
          } else {
            currentRange.end = frame.sampleOffset + frame.sampleCount;
            continue;
          }
        }

        currentRange = {
          start: frame.sampleOffset,
          end: frame.sampleOffset + frame.sampleCount,
        };
      }
      if (currentRange !== null) {
        ranges.push(currentRange);
        currentRange = null;
      }
      return ranges;
    },
    remove(startSampleOffset: number, endSampleOffset: number): void {
      logger.debug(`AudioCache.remove(startSampleOffset=${startSampleOffset}, endSampleOffset=${endSampleOffset}) {`);
      if (info === null) {
        return;
      }
      deleteRange(logger, startSampleOffset, endSampleOffset - startSampleOffset, cache, info);
    },
    clear(): void {
      for (const entry of cache.splice(0)) {
        entry.buffer.close();
      }
    },
    close(): void {
      this.clear();
    }
  };
}

function trimEntryStart(
  entry: CacheEntry,
  info: AudioInfoV2,
  sampleCount: number,
): CacheEntry {
  if (sampleCount < 0) {
    throw new Error('Trimming negative sample count');
  }
  if (sampleCount >= entry.sampleCount) {
    throw new Error('Trimming too much from start of audio frame');
  }

  const offset = calculateRawAudioFrameBufferSize(info, sampleCount);

  return {
    sampleCount: (entry.sampleCount - sampleCount),
    sampleOffset: (entry.sampleOffset + sampleCount),
    bufferOffset: entry.bufferOffset + offset,
    buffer: entry.buffer.addReference(),
  };
}

function trimEntryEnd(entry: CacheEntry, sampleCount: number): CacheEntry {
  if (sampleCount < 0) {
    throw new Error('Trimming negative sample count');
  }
  if (sampleCount >= entry.sampleCount) {
    throw new Error('Trimming too much from start of audio frame');
  }

  return {
    sampleCount: entry.sampleCount - sampleCount,
    sampleOffset: entry.sampleOffset,
    bufferOffset: entry.bufferOffset,
    buffer: entry.buffer.addReference(),
  };
}

function deleteRange(
  logger: Logger,
  sampleOffset: number,
  sampleCount: number,
  cache: CacheEntry[],
  info: AudioInfoV2,
) {
  logger.debug(`  deleteRange(sampleOffset=${sampleOffset}, sampleCount=${sampleCount}) {`);
  const start = sampleOffset;
  const end = sampleOffset + sampleCount;
  let frameIndex = 0;

  // When we split a entry in two we create new frames that we will
  // push onto the cache after the main lop
  const newEntries : CacheEntry[] = [];
  while (true) {
    const entry = cache[frameIndex];
    if (entry === undefined) {
      break;
    }
    const frameStart = entry.sampleOffset;
    const frameEnd = entry.sampleOffset + entry.sampleCount;

    logger.debug(`    deleteRange: range=[${start}, ${end}>, entry=[${frameStart}, ${frameEnd}>`);

    if ((start <= frameStart) && (end >= frameEnd)) {
      // range:     |------------|
      // entry:          |---|
      // delete:         |---|
      // keep:
      logger.debug(`    whole entry removed`);
      cache.splice(frameIndex, 1);
      entry.buffer.close();
      continue;
    } else if ((start > frameStart) && (start < frameEnd)) {
      // range:         |---..?
      // entry:       |-------|
      // delete:        |-----|
      // keep:        |-|
      const toRemove = frameEnd - start;
      logger.debug(`    remove ${toRemove} samples from end of entry`);
      cache[frameIndex] = trimEntryEnd(entry, toRemove);
      if ((end >= frameStart) && (end < frameEnd)) {
        // range:         |---|
        // entry:       |-------|
        // delete:      |-----|
        // keep:              |-|
        const toRemove = end - frameStart;
        logger.debug(`    duplicate and remove ${toRemove} samples from start of entry`);
        newEntries.push(trimEntryStart(entry, info, toRemove));
      }
      entry.buffer.close();
    } else if ((end > frameStart) && (end < frameEnd)) {
      // range:       |---|
      // entry:       |-------|
      // delete:      |---|
      // keep:            |---|
      const toRemove = end - frameStart;
      logger.debug(`    remove ${toRemove} samples from start of entry`);
      cache[frameIndex] =  trimEntryStart(entry, info, toRemove);
      entry.buffer.close();
    }
    frameIndex += 1;
  }

  if (newEntries.length > 0) {
    cache.push(...newEntries);
    cache.sort((a, b) => a.sampleOffset - b.sampleOffset);
  }
  logger.debug('  }');
  return cache;
}

function getRange(
  logger: Logger,
  sampleOffset: number,
  sampleCount: number,
  info: AudioInfoV2,
  cache: CacheEntry[],
  bufferCache: BufferCache,
): AudioFrameV2 | null {
  logger.debug(`  getRange(sampleOffset=${sampleOffset}, sampleCount=${sampleCount} {`);
  let start = sampleOffset;
  const end = sampleOffset + sampleCount;
  const entries : CacheEntry[] = [];
  let currentEnd = sampleOffset;
  for (const frame of cache) {
    const frameStart = frame.sampleOffset;
    const frameEnd = frame.sampleOffset + frame.sampleCount;

    logger.debug(`    range=[${start}, ${end}>, frame=[${frameStart}, ${frameEnd}>`);

    if (end <= frameStart) {
      logger.debug('    End before start of frame. None of the upcoming entries are relevant');
      // we're done
      break;
    }

    if (start < frameStart) {
      logger.debug('    Start range before frame, samples not available');
      // Requested range starts before the current frame rate
      logger.debug('  }');
      entries.forEach(e => e.buffer.close());
      return null;
    }

    if (start >= frameEnd) {
      logger.debug('    Before start range');
      continue;
    }

    let trimmedFrame : CacheEntry = {
      ...frame,
      buffer: frame.buffer.addReference()
    };

    if (start > frameStart) {
      logger.debug(`    trimEntryStart(count=${start - frameStart})`);
      const tmp = trimEntryStart(trimmedFrame, info, start - frameStart);
      trimmedFrame.buffer.close();
      trimmedFrame = tmp;
    }
    if (end < frameEnd) {
      logger.debug(`    trimEntryEnd(count=${frameEnd - end})`);
      const tmp = trimEntryEnd(trimmedFrame, frameEnd - end);
      trimmedFrame.buffer.close();
      trimmedFrame = tmp;
    }

    entries.push(trimmedFrame);
    currentEnd = trimmedFrame.sampleOffset + trimmedFrame.sampleCount;
    start = trimmedFrame.sampleOffset + trimmedFrame.sampleCount;
  }

  logger.debug('  }');

  if ((entries.length === 0) || (currentEnd < end)) {
    entries.forEach(e => e.buffer.close());
    return null;
  }
  invariant(currentEnd === end, `Something went wrong. currentEnd=${currentEnd}, end=${end}`);


  const audioBufferSize = calculateRawAudioFrameBufferSize(info, sampleCount);

  return {
    sampleOffset,
    sampleCount,
    sampleRate: info.sampleRate,
    channelCount: info.channelCount,
    format: info.format,
    buffer: bufferCache.allocateBuffer(
      audioBufferSize,
      (buffer: Uint8Array) => {
        let dstOffset : number = 0;
        for (const entry of entries) {
          const src = entry.buffer.asReadonlyUint8Array();
          let srcOffset = entry.bufferOffset;
          let srcBufferSize = calculateRawAudioFrameBufferSize(info, entry.sampleCount);
          entry.buffer.close();
          for (let i = 0; i < srcBufferSize; i++) {
            buffer[dstOffset] = src[srcOffset];
            srcOffset ++;
            dstOffset ++;
          }
        }
      }
    ),
  };
}

